// Internal audit register (ISO 9001 clause 9.2). Office/admin. A filterable list of
// internal audits with a New audit modal and a per-row View/edit modal (which also moves
// the status forward and deletes/archives). Self-contained: no separate detail route.
//
// SESSION AUJ (design system Session 2, pilot for the Compliance registers): PageHeader, Button, ListToolbar
// (search; the status chips stay in its filter slot and stay a server filter), sortable headers, the
// registry's `internalAudit` domain (open blue, actions pending amber, closed green: no colour moved, the
// word is now a StatusPill rather than coloured text), Loader, EmptyState and lib/format dates (an audit
// date prints "5 Sep 2026" where it printed "05 Sep 2026"). Presentation only: no route, call, permission
// or word of copy changed.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import {
  api,
  ApiError,
  type InternalAuditListItem,
  type InternalAuditDetail,
  type InternalAuditStatus,
  type InternalAuditCreateInput,
} from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";

const STATUS_LABEL: Record<InternalAuditStatus, string> = {
  open: "Open",
  actions_pending: "Actions pending",
  closed: "Closed",
};

// Search and sort run client-side over what the server returned for the status filter. Status sorts in
// its working order, dates as timestamps (the ISO strings sort the same way).
const STATUS_RANK: Record<InternalAuditStatus, number> = { open: 0, actions_pending: 1, closed: 2 };
const AUDIT_SEARCH: SearchAccessors<InternalAuditListItem> = [
  (r) => r.auditRef,
  (r) => r.auditedArea,
  (r) => r.auditorName,
  (r) => statusEntry("internalAudit", r.status).label,
];
const AUDIT_SORT: SortAccessors<InternalAuditListItem> = {
  ref: (r) => r.auditRef,
  date: (r) => r.auditDate,
  area: (r) => r.auditedArea,
  auditor: (r) => r.auditorName,
  status: (r) => STATUS_RANK[r.status],
  due: (r) => r.actionsDueDate,
};

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "actions_pending", label: "Actions pending" },
  { value: "closed", label: "Closed" },
];

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function InternalAuditsPage() {
  const [rows, setRows] = useState<InternalAuditListItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("internal-audits");

  const load = useCallback(() => {
    setLoadError(false);
    api.internalAudits
      .list((status || "all") as InternalAuditStatus | "all")
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = rows === null ? [] : sortRows(filterRows(rows, search, AUDIT_SEARCH), AUDIT_SORT, sort);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        className="mb-4"
        title="Internal audits"
        subtitle="Internal management-system audits and follow-up actions (ISO 9001 clause 9.2)."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            New audit
          </Button>
        }
      />

      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reference, area or auditor..."
        searchLabel="Search internal audits"
        resultCount={rows ? { shown: shown.length, total: rows.length, noun: "audits" } : undefined}
        sort={{
          state: sort,
          options: [
            { key: "ref", label: "Reference" },
            { key: "date", label: "Audit date" },
            { key: "status", label: "Status" },
          ],
          onChange: (st) => sort.setSort(st.sortKey, st.sortDir),
        }}
        onClear={() => setSearch("")}
        filters={
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                onClick={() => setStatus(f.value)}
                className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                  status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load internal audits. Please try again.
        </p>
      ) : rows === null ? (
        <Loader label="Loading the audits..." />
      ) : rows.length === 0 ? (
        <EmptyState message="No internal audits recorded." />
      ) : shown.length === 0 ? (
        <EmptyState role="status" message={`No internal audits match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Area, Auditor and Actions due are desktop-only below md, leaving Reference/Audit
        // date/Status to divide a phone width. The card restores Area as the second line and
        // Auditor/Actions due to the details block, mirroring the Invoices list precedent.
        <MobileCardList label="Internal audits">
          {shown.map((r) => (
            <MobileListCard
              key={r.id}
              onClick={() => setEditId(r.id)}
              primary={r.auditRef}
              secondary={r.auditedArea}
              meta={`Audited ${formatDate(r.auditDate)}`}
              right={<StatusPill domain="internalAudit" status={r.status} />}
              details={
                <div className="space-y-1 text-xs text-ele-body">
                  <p>Auditor: {r.auditorName}</p>
                  {r.actionsDueDate && <p>Actions due: {formatDate(r.actionsDueDate)}</p>}
                </div>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "14%" }} />
              <col className="max-md:hidden" style={{ width: "26%" }} />
              <col className="max-md:hidden" style={{ width: "18%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Audit date" colKey="date" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Area" colKey="area" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Auditor" colKey="auditor" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Actions due" colKey="due" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} onClick={() => setEditId(r.id)} className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface">
                  <td className="px-3 py-2 font-semibold text-ele-strong">{r.auditRef}</td>
                  <td className="px-3 py-2 text-ele-body">{formatDate(r.auditDate)}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{r.auditedArea}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{r.auditorName}</td>
                  <td className="cell-actions px-3 py-2">
                    <StatusPill domain="internalAudit" status={r.status} />
                  </td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{formatDate(r.actionsDueDate)}</td>
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AuditModal
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      )}
      {editId && (
        <AuditModal
          id={editId}
          onClose={() => setEditId(null)}
          onSaved={() => {
            setEditId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// One modal does both create (no id) and view/edit (with id). On edit it loads the detail,
// shows the forward-only status control and a Delete (which hard-deletes or archives).
function AuditModal({ id, onClose, onSaved }: { id?: string; onClose: () => void; onSaved: () => void }) {
  const editing = id != null;
  const [form, setForm] = useState({
    auditDate: "",
    auditedArea: "",
    auditorName: "",
    findings: "",
    actionsRequired: "",
    actionsDueDate: "",
    actionsCompletedDate: "",
    status: "open" as InternalAuditStatus,
  });
  const [allowedNext, setAllowedNext] = useState<InternalAuditStatus[]>([]);
  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.internalAudits
      .get(id)
      .then((d: InternalAuditDetail) => {
        setForm({
          auditDate: d.auditDate ? d.auditDate.slice(0, 10) : "",
          auditedArea: d.auditedArea,
          auditorName: d.auditorName,
          findings: d.findings,
          actionsRequired: d.actionsRequired ?? "",
          actionsDueDate: d.actionsDueDate ? d.actionsDueDate.slice(0, 10) : "",
          actionsCompletedDate: d.actionsCompletedDate ? d.actionsCompletedDate.slice(0, 10) : "",
          status: d.status,
        });
        setAllowedNext(d.allowedNextStatuses);
      })
      .catch(() => setErr("Could not load this audit."))
      .finally(() => setLoading(false));
  }, [id]);

  const set =
    (name: keyof typeof form) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.auditDate || !form.auditedArea.trim() || !form.auditorName.trim() || !form.findings.trim()) {
      setErr("Audit date, area, auditor and findings are required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (editing && id) {
        await api.internalAudits.update(id, {
          auditDate: form.auditDate,
          auditedArea: form.auditedArea.trim(),
          auditorName: form.auditorName.trim(),
          findings: form.findings.trim(),
          actionsRequired: form.actionsRequired.trim() || null,
          actionsDueDate: form.actionsDueDate || null,
          actionsCompletedDate: form.actionsCompletedDate || null,
          status: form.status,
        });
      } else {
        const body: InternalAuditCreateInput = {
          auditDate: form.auditDate,
          auditedArea: form.auditedArea.trim(),
          auditorName: form.auditorName.trim(),
          findings: form.findings.trim(),
          actionsRequired: form.actionsRequired.trim() || null,
          actionsDueDate: form.actionsDueDate || null,
          actionsCompletedDate: form.actionsCompletedDate || null,
        };
        await api.internalAudits.create(body);
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the audit. Please try again.");
      setBusy(false);
    }
  }

  async function remove() {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      await api.internalAudits.remove(id);
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not delete the audit. Please try again.");
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  // The status select offers the current status plus its forward-only next options.
  const statusOptions: InternalAuditStatus[] = editing ? [form.status, ...allowedNext] : ["open"];

  return (
    <ModalShell ariaLabel={editing ? "Edit audit" : "New audit"} onClose={() => !busy && !confirmingDelete && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={save}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">{editing ? "Internal audit" : "New internal audit"}</h2>
        {loading ? (
          <Loader shape="panel" label="Loading the audit..." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelCls}>Audit date</span>
              <input aria-label="Audit date" type="date" value={form.auditDate} onChange={set("auditDate")} className={`${inputCls} w-full`} />
            </label>
            <label>
              <span className={labelCls}>Area audited</span>
              <input aria-label="Area audited" value={form.auditedArea} onChange={set("auditedArea")} placeholder="e.g. LOLER records" className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Auditor (who conducted it)</span>
              <input aria-label="Auditor" value={form.auditorName} onChange={set("auditorName")} className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Findings</span>
              <textarea aria-label="Findings" value={form.findings} onChange={set("findings")} rows={3} className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Actions required (optional)</span>
              <textarea aria-label="Actions required" value={form.actionsRequired} onChange={set("actionsRequired")} rows={2} className={`${inputCls} w-full`} />
            </label>
            <label>
              <span className={labelCls}>Actions due date</span>
              <input aria-label="Actions due date" type="date" value={form.actionsDueDate} onChange={set("actionsDueDate")} className={`${inputCls} w-full`} />
            </label>
            <label>
              <span className={labelCls}>Actions completed date</span>
              <input aria-label="Actions completed date" type="date" value={form.actionsCompletedDate} onChange={set("actionsCompletedDate")} className={`${inputCls} w-full`} />
            </label>
            {editing && (
              <label className="sm:col-span-2">
                <span className={labelCls}>Status</span>
                <select aria-label="Status" value={form.status} onChange={set("status")} className={`${inputCls} w-full`}>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {err && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {err}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {editing && (
              <Button variant="destructive" className={MODAL_ACTION_BTN} onClick={() => setConfirmingDelete(true)} disabled={busy}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className={MODAL_ACTION_BTN} disabled={busy || loading}>
              {busy ? "Saving..." : editing ? "Save changes" : "Create audit"}
            </Button>
          </div>
        </div>
      </form>

      <ConfirmDeleteModal
        open={confirmingDelete}
        title="Delete this audit?"
        message="An untouched open audit is permanently deleted. An audit with follow-up actions, or that has progressed, is archived (closed) instead so the record is retained."
        busy={busy}
        onConfirm={remove}
        onCancel={() => setConfirmingDelete(false)}
      />
    </ModalShell>
  );
}
